import httpClient from './httpClient';
import { getRegionalApiBaseUrl } from './regionalApiStorage';
import { BASE_URL } from '../externalConfig';
import { ContactCategoryValue } from '../components/contact-us/categories';

export interface ContactFormInput {
  category: ContactCategoryValue;
  title: string;
  description: string;
  allowContact: boolean;
  // The clinician's Cognito user-ID — used as the `x-user-id` request header
  // so the Lambda's existing `claims.patientId` check (and the rest of the
  // backend, which correlates by this value) keeps working. May be a UUID for
  // some accounts; not user-facing.
  userId: string;
  // The clinician's email from the Cognito `email` attribute. User-facing —
  // shown in the modal as "Submitting as: <email>" and propagated to Jira as
  // both `contact_email` (so triage can reply) and `clinician_id` (so the
  // ticket carries a human-readable identifier in diagnostics).
  email: string;
  // The clinician's first name, used to greet them in the acknowledgement
  // email ("Dear <name>,"). Blank is allowed — the Lambda then says "Hello,".
  name?: string;
  attachment?: { blob: Blob; filename: string; mimeType: string };
}

export interface ContactFormResult {
  ticketKey: string;
}

// `process.env.NODE_ENV` and `BASE_URL` are replaced at build time by webpack
// (see externalConfig.ts and webpack.config.js). We don't have a build-time
// portal version yet, so app_version is best-effort 'unknown'.
const PORTAL_APPLICATION_ID = 'clinician-portal';
const PORTAL_VERSION_NAME = 'unknown';
const PORTAL_VERSION_CODE = '';

function deriveFlavor(): string {
  // Mirrors clinician-portal/src/config.ts environment detection. We pick a
  // single short label that matches the cloud Pulumi stack names so triage
  // can filter Jira labels by `flavor=<stack>`.
  const host = typeof window !== 'undefined' ? window.location.host : '';
  if (host.includes('mercury')) {
    return 'mercury';
  }
  if (host.includes('localhost')) {
    return 'localhost';
  }
  if (host.includes('eu-central') || host.includes('euc1')) {
    return 'production';
  }
  if (host.includes('us-west') || host.includes('usw2')) {
    return 'production-primary';
  }
  return 'production';
}

export async function submitContactForm(input: ContactFormInput): Promise<ContactFormResult> {
  const formData = new FormData();
  formData.append('category', input.category);
  formData.append('title', input.title);
  formData.append('description', input.description);
  formData.append('allow_contact', String(input.allowContact));
  formData.append('contact_name', (input.name ?? '').trim());
  formData.append('contact_email', input.email);
  formData.append('contact_phone', '');

  // Source + clinician identity — the Lambda routes the Jira label off these
  // (see cloud/support-submit/src/jira.ts and multipart.ts).
  formData.append('source', 'clinician-portal');
  formData.append('clinician_id', input.email);

  const flavor = deriveFlavor();
  formData.append('app_version_name', PORTAL_VERSION_NAME);
  formData.append('app_version_code', PORTAL_VERSION_CODE);
  formData.append('flavor', flavor);
  formData.append(
    'is_production',
    String(flavor === 'production' || flavor === 'production-primary'),
  );
  formData.append('application_id', PORTAL_APPLICATION_ID);
  formData.append('base_url', getRegionalApiBaseUrl() || BASE_URL);

  // Android-only metadata — empty for portal submissions.
  formData.append('device_manufacturer', '');
  formData.append('device_model', '');
  formData.append('android_sdk', '');
  formData.append('android_release', '');

  formData.append('locale', navigator.language || '');
  let timezone = '';
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    // Older browsers may not expose timeZone — leave blank.
  }
  formData.append('timezone', timezone);
  formData.append('client_timestamp', new Date().toISOString());

  // Browser-specific diagnostics — added for clinician-portal so Jira
  // tickets carry the same triage value the patient app gets from
  // device_* fields.
  formData.append('user_agent', navigator.userAgent || '');
  formData.append('page_url', typeof window !== 'undefined' ? window.location.href : '');

  if (input.attachment) {
    formData.append('screenshot', input.attachment.blob, input.attachment.filename);
  }

  const response = await httpClient.post<{ ticketKey?: string }>('/support', formData, {
    // The Lambda reads patient_id from x-user-id (see
    // cloud/support-submit/src/index.ts:144). For clinician submissions we
    // send the Cognito userId here so the value matches the rest of the
    // backend — `contact_email` and `clinician_id` (above) carry the
    // human-readable email separately.
    headers: {
      'x-user-id': input.userId,
      // Let Axios/the browser set the multipart boundary automatically by
      // not pinning Content-Type. (Axios + FormData handle this when no
      // explicit content-type is provided.)
    },
  });

  return { ticketKey: response.data?.ticketKey || '' };
}

// Shape returned by GET /support — mirrors JiraSearchTicket on the Lambda.
export interface SupportTicket {
  key: string;
  summary: string;
  status: string;
  category: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * List the calling clinician's support tickets, filtered by category. The
 * Lambda searches Jira by the `clinician_id=<email>` + `tenant_id=<tid>`
 * labels written at ticket-create time, so only tickets created AFTER the
 * Lambda started writing those labels are visible.
 */
export async function fetchSupportTickets({
  userId,
  email,
  category,
}: {
  userId: string;
  email: string;
  category?: string;
}): Promise<SupportTicket[]> {
  const response = await httpClient.get<{ tickets?: SupportTicket[] }>('/support', {
    headers: {
      'x-user-id': userId,
      'x-user-email': email,
    },
    params: category ? { category } : undefined,
  });
  return response.data?.tickets ?? [];
}
